import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/authContext";
import { useNavigate } from "react-router-dom";
import "../../css/MainPage/Profile.css";
import { auth, storage, db } from "../../firebase/firebase";
import { updateProfileInformation } from "../../firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { getUserProfileData } from "../../firebase/firebaseService";
import FormGroup from "../../components/FormGroup";
import InputField from "../../components/InputField";
import SubmitButton from "../../components/SubmitButton";

const Profile = () => {
  const { userLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("personal");
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    profilePicture: "",
    age: "",
    university: "",
    graduationYear: "",
    educationalStatus: "",
    major: "",
    careerGoal: "",
    gender: "",
    race: "",
    location: { zipCode: "", city: "", state: "" },
    bio: "",
    linkedIn: "",
    demographics: [],
    gpa: "",
    testScores: [{ testName: "", score: "" }],
    activities: [{ activityName: "", description: "" }],
    householdIncome: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    if (userLoggedIn) {
      const fetchUserData = async () => {
        setIsLoading(true);
        try {
          const userData = await getUserProfileData();
          if (userData) {
            // Check if user has completed onboarding
            if (!userData.onboardingCompleted) {
              navigate("/onboarding");
              return;
            }

            setFormData((prevState) => ({
              ...prevState,
              ...userData,
              testScores: userData.testScores || [{ testName: "", score: "" }],
              activities: userData.activities || [
                { activityName: "", description: "" },
              ],
            }));
          } else {
            // No user data found, redirect to onboarding
            navigate("/onboarding");
          }
        } catch (error) {
          console.error("Error fetching user data: ", error);
          setErrorMessage("Failed to load profile data");
        } finally {
          setIsLoading(false);
        }
      };

      fetchUserData();
    }
  }, [userLoggedIn, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      demographics: checked
        ? [...prevState.demographics, value]
        : prevState.demographics.filter((item) => item !== value),
    }));
  };

  const handleProfileUpdate = async () => {
    setIsLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("No user is logged in");

      await updateProfileInformation(formData.name, formData.profilePicture);
      await setDoc(doc(db, "users", currentUser.uid), formData);

      setSuccessMessage("Profile updated successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = () => {
    navigate("/change-password");
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setErrorMessage("No user is logged in");
      return;
    }

    setIsLoading(true);
    const storageRef = ref(
      storage,
      `profilePictures/${currentUser.uid}/${file.name}`
    );
    try {
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      setFormData((prevState) => ({
        ...prevState,
        profilePicture: downloadURL,
      }));
      setSuccessMessage("Profile picture updated!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error uploading profile picture: ", error);
      setErrorMessage("Failed to upload profile picture");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestScoreChange = (index, e) => {
    const { name, value } = e.target;
    const newTestScores = formData.testScores.map((score, i) =>
      i === index ? { ...score, [name]: value } : score
    );
    setFormData((prevState) => ({
      ...prevState,
      testScores: newTestScores,
    }));
  };

  const addTestScore = () => {
    setFormData((prevState) => ({
      ...prevState,
      testScores: [...prevState.testScores, { testName: "", score: "" }],
    }));
  };

  const removeTestScore = (index) => {
    setFormData((prevState) => ({
      ...prevState,
      testScores: prevState.testScores.filter((_, i) => i !== index),
    }));
  };

  const handleZipCodeChange = async (e) => {
    const zipCode = e.target.value;
    setFormData((prevState) => ({
      ...prevState,
      location: { ...prevState.location, zipCode },
    }));

    if (zipCode.length === 5) {
      try {
        const response = await fetch(`https://api.zippopotam.us/us/${zipCode}`);
        if (response.ok) {
          const data = await response.json();
          const { "place name": city, "state abbreviation": state } =
            data.places[0];
          setFormData((prevState) => ({
            ...prevState,
            location: { zipCode, city, state },
          }));
        }
      } catch (error) {
        console.error("Error fetching location data: ", error);
      }
    }
  };

  const handleActivityChange = (index, e) => {
    const { name, value } = e.target;
    const newActivities = formData.activities.map((activity, i) =>
      i === index ? { ...activity, [name]: value } : activity
    );
    setFormData((prevState) => ({
      ...prevState,
      activities: newActivities,
    }));
  };

  const addActivity = () => {
    setFormData((prevState) => ({
      ...prevState,
      activities: [
        ...prevState.activities,
        { activityName: "", description: "" },
      ],
    }));
  };

  const removeActivity = (index) => {
    setFormData((prevState) => ({
      ...prevState,
      activities: prevState.activities.filter((_, i) => i !== index),
    }));
  };

  const getProfileCompleteness = () => {
    const requiredFields = [
      "name",
      "email",
      "university",
      "major",
      "graduationYear",
      "careerGoal",
    ];
    const filledFields = requiredFields.filter(
      (field) => formData[field] && formData[field].trim() !== ""
    );
    return Math.round((filledFields.length / requiredFields.length) * 100);
  };

  if (!userLoggedIn) {
    return (
      <div className="profile-container">
        <div className="profile-message">
          <div className="message-icon">🔒</div>
          <h3>Access Restricted</h3>
          <p>Please log in to view your profile.</p>
          <button onClick={() => navigate("/login")} className="login-button">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (isLoading && !formData.name) {
    return (
      <div className="profile-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-wrapper">
        {/* Profile Header */}
        <div className="profile-header">
          <div className="profile-header-content">
            {/* <div className="profile-picture-section">
              <div className="profile-picture">
                {formData.profilePicture ? (
                  <img src={formData.profilePicture} alt="Profile" />
                ) : (
                  <div className="profile-picture-placeholder">
                    <span>
                      {formData.name
                        ? formData.name.charAt(0).toUpperCase()
                        : "U"}
                    </span>
                  </div>
                )}
                <label
                  htmlFor="profile-picture-upload"
                  className="upload-overlay"
                >
                  <span>📷</span>
                </label>
                <input
                  id="profile-picture-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePictureUpload}
                  style={{ display: "none" }}
                />
              </div>
            </div> */}

            <div className="profile-info">
              <h1>{formData.name || "Your Name"}</h1>
              <p className="profile-subtitle">
                {formData.major && formData.university
                  ? `${formData.major} at ${formData.university}`
                  : "Complete your profile to get started"}
              </p>
              <div className="profile-stats">
                <div className="stat-item">
                  <span className="stat-value">
                    {getProfileCompleteness()}%
                  </span>
                  <span className="stat-label">Complete</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">
                    {formData.graduationYear || "----"}
                  </span>
                  <span className="stat-label">Graduation</span>
                </div>
              </div>
            </div>

            <div className="profile-actions">
              <button
                className="settings-button"
                onClick={handlePasswordChange}
              >
                ⚙️ Settings
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="progress-container">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${getProfileCompleteness()}%` }}
              ></div>
            </div>
            <span className="progress-text">Profile Completeness</span>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="profile-nav">
          <button
            className={`nav-tab ${activeTab === "personal" ? "active" : ""}`}
            onClick={() => setActiveTab("personal")}
          >
            👤 Personal Info
          </button>
          <button
            className={`nav-tab ${activeTab === "academic" ? "active" : ""}`}
            onClick={() => setActiveTab("academic")}
          >
            🎓 Academic
          </button>
          <button
            className={`nav-tab ${activeTab === "activities" ? "active" : ""}`}
            onClick={() => setActiveTab("activities")}
          >
            🏆 Activities
          </button>
          <button
            className={`nav-tab ${activeTab === "preferences" ? "active" : ""}`}
            onClick={() => setActiveTab("preferences")}
          >
            ⚡ Demographics
          </button>
        </div>

        {/* Content Area */}
        <div className="profile-content">
          {/* Personal Information Tab */}
          {activeTab === "personal" && (
            <div className="tab-content">
              <div className="section-header">
                <h2>Personal Information</h2>
                <p>Edit your basic information below</p>
              </div>

              <div className="form-grid">
                <FormGroup>
                  <InputField
                    type="text"
                    name="name"
                    label="Full Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <InputField
                    type="email"
                    name="email"
                    label="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={true}
                  />
                </FormGroup>

                <FormGroup>
                  <InputField
                    type="number"
                    name="age"
                    label="Age"
                    value={formData.age}
                    onChange={handleInputChange}
                    placeholder="Enter your age"
                  />
                </FormGroup>

                <FormGroup>
                  <InputField
                    type="text"
                    name="location.zipCode"
                    label="Zip Code"
                    value={formData.location?.zipCode || ""}
                    onChange={handleZipCodeChange}
                  />
                  {formData.location?.city && formData.location?.state && (
                    <div className="location-display">
                      📍 {formData.location.city}, {formData.location.state}
                    </div>
                  )}
                </FormGroup>
              </div>

              <FormGroup>
                <label>Bio</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  placeholder="Tell us about yourself..."
                  className="bio-textarea"
                  rows="4"
                />
              </FormGroup>

              <FormGroup>
                <InputField
                  type="url"
                  name="linkedIn"
                  label="LinkedIn Profile"
                  value={formData.linkedIn}
                  onChange={handleInputChange}
                  placeholder="https://linkedin.com/in/yourprofile"
                />
              </FormGroup>
            </div>
          )}

          {/* Academic Information Tab */}
          {activeTab === "academic" && (
            <div className="tab-content">
              <div className="section-header">
                <h2>Academic Information</h2>
                <p>Edit your educational background and achievements</p>
              </div>

              <div className="form-grid">
                <FormGroup>
                  <InputField
                    type="text"
                    name="university"
                    label="University/College"
                    value={formData.university}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <InputField
                    type="text"
                    name="major"
                    label="Major/Field of Study"
                    value={formData.major}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <InputField
                    type="text"
                    name="graduationYear"
                    label="Graduation Year"
                    value={formData.graduationYear}
                    onChange={handleInputChange}
                    placeholder="e.g., 2025"
                  />
                </FormGroup>

                <FormGroup>
                  <InputField
                    type="text"
                    name="gpa"
                    label="GPA"
                    value={formData.gpa}
                    onChange={handleInputChange}
                    placeholder="e.g., 3.75"
                  />
                </FormGroup>
              </div>

              <FormGroup>
                <InputField
                  type="text"
                  name="careerGoal"
                  label="Career Goal"
                  value={formData.careerGoal}
                  onChange={handleInputChange}
                  placeholder="What's your dream career?"
                />
              </FormGroup>

              {/* Test Scores Section */}
              <div className="subsection">
                <h3>Test Scores</h3>
                {formData.testScores.map((score, index) => (
                  <div key={index} className="dynamic-form-row">
                    <FormGroup>
                      <InputField
                        type="text"
                        name="testName"
                        label="Test Name"
                        value={score.testName}
                        onChange={(e) => handleTestScoreChange(index, e)}
                        placeholder="e.g., SAT, GRE, GMAT"
                      />
                    </FormGroup>
                    <FormGroup>
                      <InputField
                        type="text"
                        name="score"
                        label="Score"
                        value={score.score}
                        onChange={(e) => handleTestScoreChange(index, e)}
                        placeholder="Your score"
                      />
                    </FormGroup>
                    <button
                      type="button"
                      onClick={() => removeTestScore(index)}
                      className="remove-button"
                    >
                      ❌
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addTestScore}
                  className="add-button"
                >
                  ➕ Add Test Score
                </button>
              </div>
            </div>
          )}

          {/* Activities Tab */}
          {activeTab === "activities" && (
            <div className="tab-content">
              <div className="section-header">
                <h2>Activities & Achievements</h2>
                <p>Edit your extracurricular activities and accomplishments</p>
              </div>

              <div className="activities-section">
                {formData.activities.map((activity, index) => (
                  <div key={index} className="activity-card">
                    <div className="activity-header">
                      <FormGroup>
                        <InputField
                          type="text"
                          name="activityName"
                          label="Activity/Achievement Name"
                          value={activity.activityName}
                          onChange={(e) => handleActivityChange(index, e)}
                          placeholder="e.g., Student Government, Research Project"
                        />
                      </FormGroup>
                      <button
                        type="button"
                        onClick={() => removeActivity(index)}
                        className="remove-button"
                      >
                        ❌
                      </button>
                    </div>
                    <FormGroup>
                      <label>Description</label>
                      <textarea
                        name="description"
                        value={activity.description}
                        onChange={(e) => handleActivityChange(index, e)}
                        placeholder="Describe your role, achievements, and impact..."
                        className="activity-description"
                        rows="3"
                      />
                    </FormGroup>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addActivity}
                  className="add-button"
                >
                  ➕ Add Activity
                </button>
              </div>
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === "preferences" && (
            <div className="tab-content">
              <div className="section-header">
                <h2>Demographics</h2>
                <p>
                  Edit your demographic information to help us personalize your
                  experience
                </p>
              </div>

              <div className="form-grid">
                <FormGroup>
                  <label>Gender</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="select-field"
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="non-binary">Non-binary</option>
                    <option value="prefer-not-to-say">Prefer not to say</option>
                  </select>
                </FormGroup>

                <FormGroup>
                  <label>Race/Ethnicity</label>
                  <select
                    name="race"
                    value={formData.race}
                    onChange={handleInputChange}
                    className="select-field"
                  >
                    <option value="">Select Race/Ethnicity</option>
                    <option value="american-indian">
                      American Indian or Alaska Native
                    </option>
                    <option value="asian">Asian</option>
                    <option value="black">Black or African American</option>
                    <option value="hispanic">Hispanic or Latino</option>
                    <option value="pacific-islander">
                      Native Hawaiian or Pacific Islander
                    </option>
                    <option value="white">White</option>
                    <option value="two-or-more">Two or more races</option>
                    <option value="prefer-not-to-say">Prefer not to say</option>
                  </select>
                </FormGroup>

                <FormGroup>
                  <label>Household Income Range</label>
                  <select
                    name="householdIncome"
                    value={formData.householdIncome}
                    onChange={handleInputChange}
                    className="select-field"
                  >
                    <option value="">Select Income Range</option>
                    <option value="under-25k">Under $25,000</option>
                    <option value="25k-50k">$25,000 - $50,000</option>
                    <option value="50k-75k">$50,000 - $75,000</option>
                    <option value="75k-100k">$75,000 - $100,000</option>
                    <option value="100k-150k">$100,000 - $150,000</option>
                    <option value="over-150k">Over $150,000</option>
                    <option value="prefer-not-to-say">Prefer not to say</option>
                  </select>
                </FormGroup>

                <FormGroup>
                  <label>Educational Status</label>
                  <select
                    name="educationalStatus"
                    value={formData.educationalStatus}
                    onChange={handleInputChange}
                    className="select-field"
                  >
                    <option value="">Select Status</option>
                    <option value="high-school">High School Student</option>
                    <option value="freshman">College Freshman</option>
                    <option value="sophomore">College Sophomore</option>
                    <option value="junior">College Junior</option>
                    <option value="senior">College Senior</option>
                    <option value="graduate">Graduate Student</option>
                    <option value="recent-graduate">Recent Graduate</option>
                  </select>
                </FormGroup>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="action-buttons">
            <button
              onClick={handleProfileUpdate}
              disabled={isLoading}
              className="save-button"
            >
              {isLoading ? "💾 Saving..." : "💾 Save Changes"}
            </button>
          </div>

          {/* Messages */}
          {errorMessage && (
            <div className="message error-message">⚠️ {errorMessage}</div>
          )}
          {successMessage && (
            <div className="message success-message">✅ {successMessage}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
